import { ZeroProvider } from "@rocicorp/zero/react";
import { RouterProvider } from "@tanstack/react-router";
import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom/client";

import { DuplicateWindowNotice } from "./components/DuplicateWindowNotice";
import { useDuplicateWindow } from "./hooks/useDuplicateWindow";
import "./lib/analytics";
import { authClient } from "./lib/auth-client";
import { fetchZeroToken, zeroCacheURL, zeroTokenRefreshDelay } from "./lib/zero";
import { router } from "./router";
import { schema } from "./zero/schema";
import "./styles.css";

// Wraps the app in a Zero client bound to the signed-in user. The JWT (minted by
// the api from the cookie session) is fetched into state and passed as `auth`;
// Zero refreshes when it changes. We only go "authenticated" once BOTH the
// userID and a token are present so we never connect half-credentialed.
function ZeroRoot() {
  // Park this window behind a notice if cotty is already open elsewhere, so two
  // instances never run duplicate terminals / race the workspace save.
  const { duplicate, takeOver } = useDuplicateWindow();
  const { data: session } = authClient.useSession();
  const userID = session?.user?.id;
  const [auth, setAuth] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!userID) {
      setAuth(undefined);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let inFlight = false;
    let attempt = 0;
    // When the CURRENT chain intends to refresh — lets wake events tell a
    // missed deadline (timer slept through it) apart from a fresh token.
    let nextRefreshAt = 0;

    // One failed fetch used to disable sync for the rest of the session (the
    // only retry was a 50-minute tick), which is how a transient blip at
    // sign-in turned into a permanently empty host list. Retry with backoff.
    // Exactly ONE chain may exist: load() clears any pending timer before
    // scheduling its successor and refuses to overlap an in-flight fetch —
    // otherwise every wake event would fork a new perpetual chain (orphaned
    // timers, parallel hourly re-mints, N-way retry storms during outages).
    const load = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      if (timer) {
        clearTimeout(timer);
        timer = undefined;
      }
      try {
        const token = await fetchZeroToken();
        if (cancelled) return;
        if (!token) {
          attempt += 1;
          const delay = Math.min(30_000, 1_000 * 2 ** attempt);
          nextRefreshAt = Date.now() + delay;
          timer = setTimeout(() => void load(), delay);
          return;
        }
        attempt = 0;
        setAuth(token);
        const delay = zeroTokenRefreshDelay(token);
        nextRefreshAt = Date.now() + delay;
        timer = setTimeout(() => void load(), delay);
      } finally {
        inFlight = false;
      }
    };
    void load();

    // Re-check when the machine comes back (network up / window shown): a
    // background-throttled timer can sleep straight past the refresh
    // deadline. Only when actually PAST due — alt-tabbing with a fresh token
    // must not re-mint (and must not bypass the failure backoff).
    const onWake = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() >= nextRefreshAt) void load();
    };
    window.addEventListener("online", onWake);
    document.addEventListener("visibilitychange", onWake);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener("online", onWake);
      document.removeEventListener("visibilitychange", onWake);
    };
  }, [userID]);

  // If this window becomes a duplicate (parked), release any live terminals it
  // was holding so the active window solely owns the sessions. SFTP panes need
  // no equivalent: they're REST-per-operation and hold no sockets or handles.
  useEffect(() => {
    if (!duplicate) return;
    void import("./lib/terminalManager").then(({ terminalManager }) =>
      terminalManager.disposeAll(),
    );
  }, [duplicate]);

  const ready = !!userID && !!auth;
  const providerUserID = ready ? userID! : null;
  const providerAuth = ready ? auth! : null;
  // Stable references — a fresh object here makes ZeroProvider tear down and
  // recreate the Zero instance on every render (connection "flapping").
  const context = useMemo(
    () => ({ userID: providerUserID ?? "" }),
    [providerUserID],
  );

  // The public deletion page must never park behind the duplicate-window
  // notice: the emailed confirmation link deliberately opens a second tab
  // while the app sits on "check your email" in the first one, and OAuth
  // reviewers may load the public URL with the app open elsewhere. The page
  // holds no terminals or workspace state, so duplicates are harmless there.
  const onDeletionPage =
    typeof window !== "undefined" &&
    window.location.pathname === "/delete-account";
  if (duplicate && !onDeletionPage) {
    return <DuplicateWindowNotice onUseHere={takeOver} />;
  }

  return (
    <ZeroProvider
      userID={providerUserID}
      auth={providerAuth}
      context={context}
      cacheURL={zeroCacheURL}
      schema={schema}
    >
      <RouterProvider router={router} />
    </ZeroProvider>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ZeroRoot />
  </React.StrictMode>,
);
